import { ReactNode } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { X, AlertTriangle, Info, CheckCircle, XCircle } from 'lucide-react'
import Button from './Button'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  children: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /** If false, clicking overlay or pressing Escape won't close the modal */
  closeOnOutsideClick?: boolean
  /** Optional description for screen readers */
  description?: string
}

/**
 * Accessible Modal built on Radix Dialog.
 *
 * Provides:
 * - role="dialog" + aria-modal="true" (via Radix)
 * - Focus trap while open + focus restoration on close
 * - Escape key to close
 * - Portal rendering to document.body
 * - Smooth fade + zoom transitions
 *
 * Backward-compatible API: preserves original props (isOpen, onClose, title, children, size).
 */
export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  closeOnOutsideClick = true,
  description,
}: ModalProps) {
  const sizes = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl',
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) onClose()
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm transition-opacity duration-200 data-[state=closed]:opacity-0 data-[state=open]:opacity-100"
        />
        <Dialog.Content
          className={`fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 ${sizes[size]} max-h-[90vh] overflow-hidden bg-background rounded-xl shadow-2xl border border-border transition-all duration-200 data-[state=closed]:opacity-0 data-[state=closed]:scale-95 data-[state=open]:opacity-100 data-[state=open]:scale-100`}
          onPointerDownOutside={(event) => {
            if (!closeOnOutsideClick) event.preventDefault()
          }}
          onEscapeKeyDown={(event) => {
            if (!closeOnOutsideClick) event.preventDefault()
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <Dialog.Title className="text-xl font-semibold text-foreground">
              {title}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Cerrar modal"
                className="p-1.5 hover:bg-muted rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </Dialog.Close>
          </div>

          {/* Description (visually hidden but read by screen readers when provided) */}
          {description ? (
            <Dialog.Description className="sr-only">
              {description}
            </Dialog.Description>
          ) : null}

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-8rem)]">
            {children}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// Confirm Modal Component
interface ConfirmModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  variant?: 'danger' | 'warning' | 'info' | 'success'
  isLoading?: boolean
}

export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  variant = 'danger',
  isLoading = false,
}: ConfirmModalProps) {
  const icons = {
    danger: <XCircle className="w-12 h-12 text-destructive" />,
    warning: <AlertTriangle className="w-12 h-12 text-amber-500" />,
    info: <Info className="w-12 h-12 text-blue-500" />,
    success: <CheckCircle className="w-12 h-12 text-green-500" />,
  }

  const confirmVariant = variant === 'danger' ? 'danger' : 'primary'

  const handleOpenChange = (open: boolean) => {
    if (!open && !isLoading) onClose()
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm transition-opacity duration-200 data-[state=closed]:opacity-0 data-[state=open]:opacity-100"
        />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 max-w-md bg-background rounded-xl shadow-2xl border border-border transition-all duration-200 data-[state=closed]:opacity-0 data-[state=closed]:scale-95 data-[state=open]:opacity-100 data-[state=open]:scale-100"
          onPointerDownOutside={(event) => {
            if (isLoading) event.preventDefault()
          }}
          onEscapeKeyDown={(event) => {
            if (isLoading) event.preventDefault()
          }}
        >
          {/* Content */}
          <div className="p-6 text-center">
            <div className="flex justify-center mb-4">
              {icons[variant]}
            </div>
            <Dialog.Title className="text-lg font-semibold text-foreground mb-2">
              {title}
            </Dialog.Title>
            <Dialog.Description className="text-muted-foreground">
              {message}
            </Dialog.Description>
          </div>

          {/* Actions */}
          <div className="flex gap-3 p-4 border-t border-border bg-muted/30">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1"
            >
              {cancelText}
            </Button>
            <Button
              variant={confirmVariant}
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1"
            >
              {isLoading ? 'Procesando...' : confirmText}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
